import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import './Wishlists.css';

const Wishlists = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState('hotels'); // 'hotels' or 'spas'
  const [openModal, setOpenModal] = useState(null); // { type: 'hotel' | 'spa', id: string }
  const [wishlistData, setWishlistData] = useState({
    hotels: [],
    spas: []
  });
  const [stats, setStats] = useState({
    totalHotels: 0,
    totalSpas: 0,
    uniqueHotels: 0,
    uniqueSpas: 0,
    totalCustomers: 0
  });

  useEffect(() => {
    fetchWishlistData();
  }, []);

  const fetchWishlistData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all customers with their wishlists
      const customersResponse = await axios.get(`${API_BASE_URL}/admin/customers`, {
        params: { page: 1, limit: 1000 } // Get all customers
      });

      const customers = customersResponse.data.data || [];
      
      // Aggregate wishlist data
      const hotelsMap = new Map(); // hotelId -> { hotel, customers: [] }
      const spasMap = new Map(); // spaId -> { spa, customers: [] }
      const uniqueHotelIds = new Set();
      const uniqueSpaIds = new Set();
      let totalCustomersWithWishlist = 0;

      customers.forEach(customer => {
        const hasWishlist = (customer.wishlistHotels?.length > 0) || (customer.wishlistSpas?.length > 0);
        if (hasWishlist) {
          totalCustomersWithWishlist++;
        }

        // Process hotels
        if (customer.wishlistHotels && customer.wishlistHotels.length > 0) {
          customer.wishlistHotels.forEach(hotelId => {
            uniqueHotelIds.add(hotelId.toString());
            if (!hotelsMap.has(hotelId.toString())) {
              hotelsMap.set(hotelId.toString(), {
                hotelId: hotelId.toString(),
                customers: []
              });
            }
            hotelsMap.get(hotelId.toString()).customers.push({
              id: customer._id,
              name: customer.displayName || customer.name || 'Unknown',
              displayName: customer.displayName,
              email: customer.email,
              phone: customer.phone,
              profilePicture: customer.profilePicture,
              isActive: customer.isActive
            });
          });
        }

        // Process spas
        if (customer.wishlistSpas && customer.wishlistSpas.length > 0) {
          customer.wishlistSpas.forEach(spaId => {
            uniqueSpaIds.add(spaId.toString());
            if (!spasMap.has(spaId.toString())) {
              spasMap.set(spaId.toString(), {
                spaId: spaId.toString(),
                customers: []
              });
            }
            spasMap.get(spaId.toString()).customers.push({
              id: customer._id,
              name: customer.displayName || customer.name || 'Unknown',
              displayName: customer.displayName,
              email: customer.email,
              phone: customer.phone,
              profilePicture: customer.profilePicture,
              isActive: customer.isActive
            });
          });
        }
      });

      // Fetch all hotels and spas at once
      const [hotelsResponse, spasResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/hotels`),
        axios.get(`${API_BASE_URL}/admin/spas`)
      ]);

      const allHotels = hotelsResponse.data.data || [];
      const allSpas = spasResponse.data.data || [];

      // Calculate minPricePerNight for hotels
      const hotelsWithPrices = allHotels.map(hotel => {
        let minPricePerNight = 0;
        if (hotel.roomTypes && hotel.roomTypes.length > 0) {
          minPricePerNight = Math.min(
            ...hotel.roomTypes.map(room => room.pricePerNight || 0).filter(price => price > 0)
          );
        }
        return {
          ...hotel,
          minPricePerNight: minPricePerNight || hotel.minPricePerNight || 0
        };
      });

      // Filter and map hotels that are in wishlist
      const hotelsData = hotelsWithPrices
        .filter(hotel => uniqueHotelIds.has(hotel._id.toString()))
        .map(hotel => ({
          ...hotel,
          wishlistCount: hotelsMap.get(hotel._id.toString())?.customers.length || 0,
          customers: hotelsMap.get(hotel._id.toString())?.customers || []
        }));

      // Calculate min/max price for spas
      const spasWithPrices = allSpas.map(spa => {
        let minPrice = 0;
        let maxPrice = 0;
        if (spa.services && spa.services.length > 0) {
          const prices = spa.services.map(service => service.price || 0).filter(price => price > 0);
          if (prices.length > 0) {
            minPrice = Math.min(...prices);
            maxPrice = Math.max(...prices);
          }
        }
        return {
          ...spa,
          minPrice: minPrice || spa.minPrice || 0,
          maxPrice: maxPrice || spa.maxPrice || 0
        };
      });

      // Filter and map spas that are in wishlist
      const spasData = spasWithPrices
        .filter(spa => uniqueSpaIds.has(spa._id.toString()))
        .map(spa => ({
          ...spa,
          wishlistCount: spasMap.get(spa._id.toString())?.customers.length || 0,
          customers: spasMap.get(spa._id.toString())?.customers || []
        }));

      // Sort by wishlist count (most popular first)
      hotelsData.sort((a, b) => b.wishlistCount - a.wishlistCount);
      spasData.sort((a, b) => b.wishlistCount - a.wishlistCount);

      setWishlistData({
        hotels: hotelsData,
        spas: spasData
      });

      setStats({
        totalHotels: hotelsData.reduce((sum, h) => sum + h.wishlistCount, 0),
        totalSpas: spasData.reduce((sum, s) => sum + s.wishlistCount, 0),
        uniqueHotels: hotelsData.length,
        uniqueSpas: spasData.length,
        totalCustomers: totalCustomersWithWishlist
      });

      setLoading(false);
    } catch (error) {
      setError('Failed to fetch wishlist data');
      setLoading(false);
      console.error('Error fetching wishlist data:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container">
          <div className="wishlists-header">
            <h1>Wishlists</h1>
            <div className="header-stats">
              <div className="skeleton skeleton-text" style={{ width: '120px', height: '36px', borderRadius: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '120px', height: '36px', borderRadius: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '120px', height: '36px', borderRadius: '8px' }}></div>
            </div>
          </div>
          <div className="wishlists-tabs">
            <div className="skeleton skeleton-text" style={{ width: '150px', height: '48px', borderRadius: '8px' }}></div>
            <div className="skeleton skeleton-text" style={{ width: '150px', height: '48px', borderRadius: '8px' }}></div>
          </div>
          <div className="wishlists-table-container">
            <table className="wishlists-table">
              <thead>
                <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Location</th>
                    <th>Price</th>
                    <th>Wishlisted By</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-image" style={{ width: '80px', height: '80px', borderRadius: '8px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '70%', height: '16px', marginBottom: '4px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '50%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80px', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '100px', height: '24px', borderRadius: '12px' }}></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="wishlists-header">
          <h1>Wishlists</h1>
          <div className="header-stats">
            <span className="stat-badge">Customers: {stats.totalCustomers}</span>
            <span className="stat-badge">Hotels: {stats.uniqueHotels}</span>
            <span className="stat-badge">Spas: {stats.uniqueSpas}</span>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Tabs */}
        <div className="wishlists-tabs">
          <button
            className={`tab-button ${selectedTab === 'hotels' ? 'active' : ''}`}
            onClick={() => setSelectedTab('hotels')}
          >
            <span className="tab-icon">🏨</span>
            Hotels ({stats.uniqueHotels})
          </button>
          <button
            className={`tab-button ${selectedTab === 'spas' ? 'active' : ''}`}
            onClick={() => setSelectedTab('spas')}
          >
            <span className="tab-icon">💆</span>
            Spas ({stats.uniqueSpas})
          </button>
        </div>

        {/* Content */}
        <div className="wishlists-content">
          {selectedTab === 'hotels' ? (
            wishlistData.hotels.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🏨</div>
                <h2>No wishlisted hotels found</h2>
                <p>No customers have wishlisted any hotels yet.</p>
              </div>
            ) : (
              <div className="wishlists-table-container">
                <table className="wishlists-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Hotel Name</th>
                      <th>Location</th>
                      <th>Price</th>
                      <th>Wishlisted By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wishlistData.hotels.map((hotel) => (
                      <tr key={hotel._id}>
                        <td>
                          <div className="wishlist-image-cell">
                            {hotel.featuredImage ? (
                              <img
                                src={`${API_BASE_URL_WITHOUT_API}${hotel.featuredImage}`}
                                alt={hotel.name}
                                className="wishlist-table-image"
                              />
                            ) : (
                              <div className="wishlist-image-placeholder">
                                <span className="placeholder-icon">🏨</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="wishlist-name-cell">
                            <span className="wishlist-name-text">{hotel.name}</span>
                          </div>
                        </td>
                        <td>
                          <div className="wishlist-location-cell">
                            <span>{hotel.city}, {hotel.state}</span>
                          </div>
                        </td>
                        <td>
                          <div className="wishlist-price-cell">
                            {hotel.minPricePerNight > 0 ? (
                              <span className="price-text">
                                {hotel.currency === 'INR' ? '₹' : hotel.currency}
                                {hotel.minPricePerNight.toFixed(0)}/night
                              </span>
                            ) : (
                              <span className="price-empty">—</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="wishlist-customers-cell">
                            <button
                              className="customers-count-btn"
                              onClick={() => setOpenModal({ type: 'hotel', id: hotel._id })}
                            >
                              {hotel.wishlistCount} {hotel.wishlistCount === 1 ? 'Customer' : 'Customers'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            wishlistData.spas.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">💆</div>
                <h2>No wishlisted spas found</h2>
                <p>No customers have wishlisted any spas yet.</p>
              </div>
            ) : (
              <div className="wishlists-table-container">
                <table className="wishlists-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Spa Name</th>
                      <th>Location</th>
                      <th>Price</th>
                      <th>Wishlisted By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wishlistData.spas.map((spa) => (
                      <tr key={spa._id}>
                        <td>
                          <div className="wishlist-image-cell">
                            {spa.featuredImage ? (
                              <img
                                src={`${API_BASE_URL_WITHOUT_API}${spa.featuredImage}`}
                                alt={spa.name}
                                className="wishlist-table-image"
                              />
                            ) : (
                              <div className="wishlist-image-placeholder">
                                <span className="placeholder-icon">💆</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="wishlist-name-cell">
                            <span className="wishlist-name-text">{spa.name}</span>
                          </div>
                        </td>
                        <td>
                          <div className="wishlist-location-cell">
                            <span>{spa.city}, {spa.state}</span>
                          </div>
                        </td>
                        <td>
                          <div className="wishlist-price-cell">
                            {spa.minPrice > 0 ? (
                              <span className="price-text">
                                {spa.currency === 'INR' ? '₹' : spa.currency}
                                {spa.minPrice.toFixed(0)}
                                {spa.maxPrice && spa.maxPrice > spa.minPrice && ` - ${spa.maxPrice.toFixed(0)}`}
                              </span>
                            ) : (
                              <span className="price-empty">—</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="wishlist-customers-cell">
                            <button
                              className="customers-count-btn"
                              onClick={() => setOpenModal({ type: 'spa', id: spa._id })}
                            >
                              {spa.wishlistCount} {spa.wishlistCount === 1 ? 'Customer' : 'Customers'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>

        {/* Customer Modals */}
        {wishlistData.hotels.map((hotel) => {
          if (openModal?.type === 'hotel' && openModal?.id === hotel._id) {
            return (
              <div key={hotel._id} className="customers-modal" onClick={() => setOpenModal(null)}>
                <div className="customers-modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="customers-modal-header">
                    <h2>Customers who wishlisted: {hotel.name}</h2>
                    <button
                      className="modal-close"
                      onClick={() => setOpenModal(null)}
                    >
                      ×
                    </button>
                  </div>
              <div className="customers-modal-body">
                <div className="customers-table-container">
                  <table className="customers-table">
                    <thead>
                      <tr>
                        <th>Profile</th>
                        <th>Name</th>
                        <th>Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hotel.customers.map((customer) => (
                        <tr key={customer.id}>
                          <td>
                            <div className="customer-profile-cell">
                              {customer.profilePicture ? (
                                <img
                                  src={`${API_BASE_URL_WITHOUT_API}${customer.profilePicture}`}
                                  alt={customer.name}
                                  className="customer-profile-image"
                                />
                              ) : (
                                <div className="customer-profile-placeholder">
                                  {(customer.name || 'C').charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="customer-name-cell">
                              <span className="customer-name-text">{customer.name}</span>
                              {customer.displayName && customer.name !== customer.displayName && (
                                <span className="customer-secondary-name">{customer.displayName}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="customer-contact-cell">
                              {customer.phone && (
                                <div className="contact-item">
                                  <a href={`tel:${customer.phone}`} className="contact-link" title="Call">
                                    <span className="contact-icon">📱</span>
                                    <span>{customer.phone}</span>
                                  </a>
                                </div>
                              )}
                              {customer.email && (
                                <div className="contact-item">
                                  <a href={`mailto:${customer.email}`} className="contact-link" title="Email">
                                    <span className="contact-icon">✉️</span>
                                    <span className="contact-email">{customer.email}</span>
                                  </a>
                                </div>
                              )}
                              {!customer.phone && !customer.email && (
                                <span className="contact-empty">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
            );
          }
          return null;
        })}

        {wishlistData.spas.map((spa) => {
          if (openModal?.type === 'spa' && openModal?.id === spa._id) {
            return (
              <div key={spa._id} className="customers-modal" onClick={() => setOpenModal(null)}>
                <div className="customers-modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="customers-modal-header">
                    <h2>Customers who wishlisted: {spa.name}</h2>
                    <button
                      className="modal-close"
                      onClick={() => setOpenModal(null)}
                    >
                      ×
                    </button>
                  </div>
              <div className="customers-modal-body">
                <div className="customers-table-container">
                  <table className="customers-table">
                    <thead>
                      <tr>
                        <th>Profile</th>
                        <th>Name</th>
                        <th>Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spa.customers.map((customer) => (
                        <tr key={customer.id}>
                          <td>
                            <div className="customer-profile-cell">
                              {customer.profilePicture ? (
                                <img
                                  src={`${API_BASE_URL_WITHOUT_API}${customer.profilePicture}`}
                                  alt={customer.name}
                                  className="customer-profile-image"
                                />
                              ) : (
                                <div className="customer-profile-placeholder">
                                  {(customer.name || 'C').charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="customer-name-cell">
                              <span className="customer-name-text">{customer.name}</span>
                              {customer.displayName && customer.name !== customer.displayName && (
                                <span className="customer-secondary-name">{customer.displayName}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="customer-contact-cell">
                              {customer.phone && (
                                <div className="contact-item">
                                  <a href={`tel:${customer.phone}`} className="contact-link" title="Call">
                                    <span className="contact-icon">📱</span>
                                    <span>{customer.phone}</span>
                                  </a>
                                </div>
                              )}
                              {customer.email && (
                                <div className="contact-item">
                                  <a href={`mailto:${customer.email}`} className="contact-link" title="Email">
                                    <span className="contact-icon">✉️</span>
                                    <span className="contact-email">{customer.email}</span>
                                  </a>
                                </div>
                              )}
                              {!customer.phone && !customer.email && (
                                <span className="contact-empty">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
            );
          }
          return null;
        })}
      </div>
    </Layout>
  );
};

export default Wishlists;

